import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { FlowcountState, Membership } from '../domain/types';
import { demoStore } from './demoStore';
import { accessToken } from '../auth/supabase';
import { useAuth } from '../auth/AuthContext';

const demoMode = import.meta.env.VITE_DEMO_MODE !== 'false' || !import.meta.env.VITE_SUPABASE_URL;

type Mutation =
  | { type: 'count'; payload: Parameters<typeof demoStore.recordPhysicalCount>[0] }
  | { type: 'delivery'; payload: Parameters<typeof demoStore.receiveDelivery>[0] }
  | { type: 'transfer'; payload: Parameters<typeof demoStore.transferStock>[0] }
  | { type: 'correction'; payload: Parameters<typeof demoStore.correctStock>[0] }
  | { type: 'problem'; payload: Parameters<typeof demoStore.reportProblem>[0] }
  | { type: 'resolve-problem'; payload: { problemId: string; actorName: string; actorType: Membership['role']; note: string } }
  | { type: 'purchase'; payload: Parameters<typeof demoStore.recordPurchase>[0] }
  | { type: 'incident'; payload: Parameters<typeof demoStore.createSupplierIncident>[0] }
  | { type: 'resolve-incident'; payload: Parameters<typeof demoStore.resolveSupplierIncident>[0] }
  | { type: 'supplier'; payload: Parameters<typeof demoStore.upsertSupplier>[0] }
  | { type: 'item-supplier'; payload: Parameters<typeof demoStore.assignItemSupplier>[0] }
  | { type: 'create-item'; payload: Parameters<typeof demoStore.createItem>[0] }
  | { type: 'item-unit'; payload: Parameters<typeof demoStore.upsertItemUnit>[0] }
  | { type: 'update-setting'; payload: { settingId: string; lowThreshold?: number | null; parLevel?: number | null; dailyCount?: boolean; stocked?: boolean; counted?: boolean; active?: boolean } };

interface DataContextValue {
  state: FlowcountState;
  membership: Membership;
  demoMode: boolean;
  productionLoaded: boolean;
  productionError: string;
  productionAccessDenied: boolean;
  platformAdmin: boolean;
  setDemoUser: (userId: string) => void;
  mutate: (mutation: Mutation) => Promise<void>;
  hydratePublic: (bootstrap: Partial<FlowcountState>) => void;
  resetDemo: () => void;
}

const DataContext = createContext<DataContextValue | null>(null);

export function DataProvider({ children }: { children: React.ReactNode }) {
  const { session } = useAuth();
  const [state, setState] = useState<FlowcountState>(() => structuredClone(demoStore.state));
  const [productionLoaded, setProductionLoaded] = useState(demoMode);
  const [productionError, setProductionError] = useState('');
  // Set when the server refuses the signed-in identity (403): an authenticated
  // Google account without an active Flowcount membership. Fail closed.
  const [productionAccessDenied, setProductionAccessDenied] = useState(false);
  const [productionPlatformAdmin, setProductionPlatformAdmin] = useState(false);
  const [demoUser, setDemoUserState] = useState(() => localStorage.getItem('flowcount-demo-user') ?? 'demo-management');
  const membership = state.memberships.find((item) => item.userId === demoUser) ?? state.memberships[0];
  const platformAdmin = demoMode ? demoUser === 'demo-management' : productionPlatformAdmin;

  const fetchProductionState = async () => {
    const token = await accessToken();
    if (!token) return;
    const response = await fetch('/api/state', { headers: { authorization: `Bearer ${token}` } });
    if (response.status === 403) {
      // Authenticated but not an active, authorised Flowcount member.
      setProductionAccessDenied(true); setProductionLoaded(true); return;
    }
    if (!response.ok) throw new Error('Could not load Flowcount workspace.');
    const next = await response.json() as FlowcountState & { platformAdmin?: boolean };
    setProductionPlatformAdmin(Boolean(next.platformAdmin));
    const { platformAdmin: _platformAdmin, ...flowcountState } = next;
    setState(flowcountState as FlowcountState); setDemoUserState(next.memberships[0]?.userId ?? ''); setProductionError(''); setProductionAccessDenied(false); setProductionLoaded(true);
  };

  useEffect(() => {
    if (!demoMode && session) {
      setProductionLoaded(false); setProductionError(''); setProductionAccessDenied(false);
      fetchProductionState().catch((error) => { console.error(error); setProductionError('Could not load Flowcount workspace.'); });
    }
  }, [session?.access_token]);

  const refreshDemo = () => setState(structuredClone(demoStore.state));
  const setDemoUser = (userId: string) => { localStorage.setItem('flowcount-demo-user', userId); setDemoUserState(userId); };

  const mutate = async (mutation: Mutation) => {
    if (!demoMode) {
      const publicActor = 'actorType' in mutation.payload && mutation.payload.actorType === 'PUBLIC_BARISTA';
      const locationId = 'locationId' in mutation.payload ? mutation.payload.locationId : '';
      const code = state.locations.find((location) => location.id === locationId)?.code;
      const token = publicActor && code ? sessionStorage.getItem(`flowcount-public-${code}`) : await accessToken();
      const path = publicActor && code ? `/api/public/${code}/actions/${mutation.type}` : `/api/actions/${mutation.type}`;
      const response = await fetch(path, {
        method: 'POST', headers: { 'content-type': 'application/json', ...(token ? { authorization: `Bearer ${token}` } : {}) }, body: JSON.stringify(mutation.payload),
      });
      if (!response.ok) throw new Error((await response.json().catch(() => null))?.error ?? 'Action failed.');
      if (!publicActor) {
        try { await fetchProductionState(); }
        catch (error) {
          console.error(error);
          setProductionLoaded(false);
          setProductionError('Your action was saved, but Flowcount could not refresh. Reload before recording another action.');
        }
      }
      return;
    }
    switch (mutation.type) {
      case 'count': demoStore.recordPhysicalCount(mutation.payload); break;
      case 'delivery': demoStore.receiveDelivery(mutation.payload); break;
      case 'transfer': demoStore.transferStock(mutation.payload); break;
      case 'correction': demoStore.correctStock(mutation.payload); break;
      case 'problem': demoStore.reportProblem(mutation.payload); break;
      case 'resolve-problem': demoStore.resolveProblem(mutation.payload.problemId, mutation.payload.actorName, mutation.payload.actorType, mutation.payload.note); break;
      case 'purchase': demoStore.recordPurchase(mutation.payload); break;
      case 'incident': demoStore.createSupplierIncident(mutation.payload); break;
      case 'resolve-incident': demoStore.resolveSupplierIncident(mutation.payload); break;
      case 'supplier': demoStore.upsertSupplier(mutation.payload); break;
      case 'item-supplier': demoStore.assignItemSupplier(mutation.payload); break;
      case 'create-item': demoStore.createItem(mutation.payload); break;
      case 'item-unit': demoStore.upsertItemUnit(mutation.payload); break;
      case 'update-setting': {
        const setting = demoStore.state.settings.find((candidate) => candidate.id === mutation.payload.settingId);
        if (!setting) throw new Error('Setting not found.');
        Object.assign(setting, mutation.payload);
        demoStore.state.audit.unshift({ id: crypto.randomUUID(), tenantId: demoStore.state.tenant.id, actorType: membership.role, actorName: membership.displayName, action: 'ITEM_LOCATION_SETTING_UPDATED', entityType: 'item_location_setting', entityId: setting.id, locationId: setting.locationId, summary: 'Item location setting updated.', createdAt: new Date().toISOString() });
        break;
      }
    }
    demoStore.save(); refreshDemo();
  };

  const hydratePublic = (bootstrap: Partial<FlowcountState>) => setState((previous) => ({ ...previous, ...bootstrap }));
  const value = useMemo(() => ({ state, membership, demoMode, productionLoaded, productionError, productionAccessDenied, platformAdmin, setDemoUser, mutate, hydratePublic, resetDemo: () => { demoStore.reset(); refreshDemo(); } }), [state, membership, demoMode, productionLoaded, productionError, productionAccessDenied, platformAdmin]);
  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
}

export function useData() {
  const value = useContext(DataContext);
  if (!value) throw new Error('useData must be inside DataProvider');
  return value;
}
